import { faCartShopping, faTrash } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { useQuery } from "@tanstack/react-query";
import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { API_BASE_URL } from "../api/config";
import Logo from "../assets/images/Logo_HIRSCH.png";

const SALUTATION_OPTIONS = ["", "Herr", "Frau", "Divers", "Keine Angabe"];

export default function CartPage() {
    const [isMenuOpen, setIsMenuOpen] = useState(false);
    const [isCheckingOut, setIsCheckingOut] = useState(false);
    const [isCancellingCheckout, setIsCancellingCheckout] = useState(false);
    const [customerType, setCustomerType] = useState("private");
    const [billingCustomerType, setBillingCustomerType] = useState("private");
    const [shippingAddress, setShippingAddress] = useState({
        salutation: '',
        companyName: '',
        firstName: '',
        lastName: '',
        street: '',
        zipCode: '',
        city: '',
        country: '',
        phone: '',
        email: ''
    });
    const [billingAddress, setBillingAddress] = useState({
        salutation: '',
        companyName: '',
        companyTaxId: '',
        companyRegisterId: '',
        firstName: '',
        lastName: '',
        street: '',
        zipCode: '',
        city: '',
        country: '',
        phone: '',
        email: ''
    });
    const [useDifferentBillingAddress, setUseDifferentBillingAddress] = useState(false);
    const [editingShipping, setEditingShipping] = useState(false);
    const [editingBilling, setEditingBilling] = useState(false);
    const [editingItem, setEditingItem] = useState(null);
    const [editingItemData, setEditingItemData] = useState({
        quantity: 1,
        startDate: '',
        endDate: ''
    });
    const [acceptedConditions1, setAcceptedConditions1] = useState(false);
    const [acceptedConditions2, setAcceptedConditions2] = useState(false);
    const [showImportantNotesModal, setShowImportantNotesModal] = useState(false);
    const [hasOpenedImportantNotesModal, setHasOpenedImportantNotesModal] = useState(false);
    const storedBookingPlz = sessionStorage.getItem("booking_plz") || localStorage.getItem("booking_plz") || "";

    const fetchCart = async () => {
        const res = await fetch(`${API_BASE_URL}/cart`, { credentials: "include" });
        if (!res.ok) {
            return null;
        }

        return res.json();
    };

    const { data: cart, isLoading, refetch } = useQuery({
        queryKey: ["cart"],
        queryFn: fetchCart,
        refetchOnWindowFocus: true,
    });

    useEffect(() => {
        if (!cart || editingShipping || editingBilling || editingItem !== null) {
            return;
        }

        setShippingAddress({
            salutation: cart.deliverySalutation || '',
            companyName: cart.deliveryCompanyName || '',
            firstName: cart.deliveryFirstName || '',
            lastName: cart.deliveryLastName || '',
            street: cart.deliveryStreet || '',
            zipCode: cart.deliveryZipCode || storedBookingPlz || '',
            city: cart.deliveryCity || '',
            country: cart.deliveryCountry || '',
            phone: cart.deliveryPhone || '',
            email: cart.deliveryEmail || ''
        });
        setBillingAddress({
            salutation: cart.billingSalutation || '',
            companyName: cart.billingCompanyName || '',
            companyTaxId: cart.billingCompanyTaxId || '',
            companyRegisterId: cart.billingCompanyRegisterId || '',
            firstName: cart.billingFirstName || '',
            lastName: cart.billingLastName || '',
            street: cart.billingStreet || '',
            zipCode: cart.billingZipCode || '',
            city: cart.billingCity || '',
            country: cart.billingCountry || '',
            phone: cart.billingPhone || '',
            email: cart.billingEmail || ''
        });
        setBillingCustomerType(cart.billingCompanyName ? "business" : "private");
    }, [cart, editingBilling, editingItem, editingShipping, storedBookingPlz]);

    const isCartPending = cart?.status === "PENDING";
    const pendingUntilText = cart?.pendingUntil
        ? new Date(cart.pendingUntil).toLocaleString("de-DE")
        : null;
    const pendingCheckoutUrl = cart?.pendingCheckoutUrl || null;

    const handleResumeCheckout = () => {
        if (!pendingCheckoutUrl) {
            alert("Es konnte keine aktive Checkout-Session gefunden werden.");
            return;
        }

        window.location.href = pendingCheckoutUrl;
    };

    const handleCancelCheckout = async () => {
        if (isCancellingCheckout) {
            return;
        }

        setIsCancellingCheckout(true);
        try {
            const response = await fetch(`${API_BASE_URL}/checkout/cancel`, {
                method: "DELETE",
                credentials: "include"
            });

            if (!response.ok) {
                await response.text();

                return;
            }

            await refetch();
        } catch (error) {
            alert("Fehler beim Abbrechen des Checkouts.");
        } finally {
            setIsCancellingCheckout(false);
            setIsCheckingOut(false);
        }
    };

    const handleCheckout = async () => {
        if (isCheckingOut) {
            return; // Already checking out, ignore duplicate clicks
        }

        if (isCartPending) {
            alert("Ihr Checkout läuft bereits. Bitte schließen Sie die Zahlung ab.");
            return;
        }

        setIsCheckingOut(true);

        const response = await fetch(`${API_BASE_URL}/checkout`, {
            method: "POST",
            credentials: "include",
            body: JSON.stringify({
                customerType,
                shippingAddress: shippingAddress,
                billingAddress: useDifferentBillingAddress ? billingAddress : shippingAddress
            }),
            headers: { "Content-Type": "application/json" }
        });

        if (!response.ok) {
            await response.text();

            setIsCheckingOut(false);
            return;
        }

        const checkoutUrl = await response.text();
        window.location.href = checkoutUrl; // Weiterleitung zu Stripe
    };

    const handleRemoveFromCart = async (cartItemId) => {
        if (isCartPending) {
            alert("Während eines laufenden Checkouts können keine Artikel entfernt werden.");
            return;
        }

        await fetch(`${API_BASE_URL}/cart/item`, {
            method: "DELETE",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({
                cartItemId: cartItemId
            }),
            credentials: "include"
        });
        await refetch();
    };

    const isEmailFormatValid = (email) => {
        const emailValue = (email || "").trim();
        return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(emailValue);
    };

    const isShippingAddressValid = () => {
        const hasValidPersonData = customerType === "business"
            ? shippingAddress.companyName?.trim() !== ''
            : shippingAddress.firstName?.trim() !== '' && shippingAddress.lastName?.trim() !== '';

        return hasValidPersonData &&
            shippingAddress.street?.trim() !== '' &&
            shippingAddress.zipCode?.trim() !== '' &&
            shippingAddress.city?.trim() !== '' &&
            shippingAddress.country?.trim() !== '' &&
            shippingAddress.phone?.trim() !== '' &&
            shippingAddress.email?.trim() !== '' &&
            isEmailFormatValid(shippingAddress.email);
    };

    const isBillingAddressValid = () => {
        if (!useDifferentBillingAddress) {
            return true; // Wenn Rechnungsadresse = Versandadresse, ist sie automatisch gültig
        }
        const hasValidBillingPersonData = billingCustomerType === "business"
            ? billingAddress.companyName?.trim() !== ''
            : billingAddress.firstName?.trim() !== '' && billingAddress.lastName?.trim() !== '';

        return hasValidBillingPersonData &&
            billingAddress.street?.trim() !== '' &&
            billingAddress.zipCode?.trim() !== '' &&
            billingAddress.city?.trim() !== '' &&
            billingAddress.country?.trim() !== '' &&
            billingAddress.email?.trim() !== '' &&
            isEmailFormatValid(billingAddress.email);
    };

    const hasInvalidShippingEmail = shippingAddress.email?.trim() !== '' && !isEmailFormatValid(shippingAddress.email);
    const hasInvalidBillingEmail = useDifferentBillingAddress
        && billingAddress.email?.trim() !== ''
        && !isEmailFormatValid(billingAddress.email);
    const hasInvalidEmailFormat = hasInvalidShippingEmail || hasInvalidBillingEmail;

    const getCheckoutHint = () => {
        if (isCartPending) {
            return { message: "Checkout läuft bereits. Bitte schließen Sie die Zahlung ab.", isError: false };
        }
        if (!hasOpenedImportantNotesModal) {
            return { message: "Bitte öffnen Sie zuerst die wichtigen Hinweise", isError: false };
        }
        if (hasInvalidEmailFormat) {
            return { message: "Bitte geben Sie eine gültige E-Mail-Adresse ein (z. B. name@beispiel.de).", isError: true };
        }
        if (!isShippingAddressValid() || !isBillingAddressValid()) {
            return { message: "Bitte füllen Sie alle Adressfelder aus", isError: false };
        }
        return { message: "", isError: false };
    };

    const checkoutHint = getCheckoutHint();

    const isCheckoutEnabled = () => {
        return !isCartPending
            && !isCheckingOut
            && isShippingAddressValid()
            && isBillingAddressValid()
            && hasOpenedImportantNotesModal
            && acceptedConditions1
            && acceptedConditions2;
    };

    // Wenn plz nicht 28... oder 28195 ist, dann ist es außerhalb von Bremen
    const isOutsideBremen = !shippingAddress.zipCode?.startsWith("28") || shippingAddress.zipCode === "28195";

    const openImportantNotesModal = () => {
        setHasOpenedImportantNotesModal(true);
        setShowImportantNotesModal(true);
    };

    const closeImportantNotesModal = () => {
        setShowImportantNotesModal(false);
    };

    const handleConditionsToggle = (conditionNumber, checked) => {
        if (!hasOpenedImportantNotesModal) {
            openImportantNotesModal();
            return;
        }

        if (conditionNumber === 1) {
            setAcceptedConditions1(checked);
            return;
        }

        setAcceptedConditions2(checked);
    };

    const renderShopHeader = () => (
        <header className="bg-white shadow-sm sticky top-0 z-50">
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                <div className="flex items-center justify-between h-32">
                    <div className="flex items-center">
                        <Link to="/" className="flex items-center">
                            <img src={Logo} alt="Hirsch Logo" className="h-12 lg:h-24" />
                        </Link>
                    </div>

                    <nav className="hidden lg:flex items-center space-x-8">
                        <a href="/#hero" className="text-gray-600 hover:text-gray-900 font-medium text-xl">
                            Jetzt buchen!
                        </a>
                        <a href="/#abfallarten" className="text-gray-600 hover:text-gray-900 font-medium text-xl">
                            Abfallarten
                        </a>
                        <a href="/#container" className="text-gray-600 hover:text-gray-900 font-medium text-xl">
                            Container
                        </a>
                        <a href="/#faq" className="text-gray-600 hover:text-gray-900 font-medium text-xl">
                            Kontakt
                        </a>
                        <Link
                            to="/cart"
                            className="bg-pink-600 text-white px-6 py-2.5 rounded-md hover:bg-pink-700 transition-colors font-bold text-sm uppercase"
                        >
                            Warenkorb
                        </Link>
                    </nav>

                    <button
                        onClick={() => setIsMenuOpen((prev) => !prev)}
                        className="lg:hidden text-gray-600 hover:text-gray-900 p-2 rounded-lg hover:bg-gray-100 transition-all"
                        aria-label="Toggle menu"
                    >
                        <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
                        </svg>
                    </button>
                </div>

                {isMenuOpen && (
                    <div className="lg:hidden py-4 border-t border-gray-100">
                        <nav className="flex flex-col space-y-2">
                            <a href="/#hero" onClick={() => setIsMenuOpen(false)} className="text-gray-600 hover:text-gray-900 hover:bg-gray-50 font-medium py-3 px-3 rounded-lg transition-all">
                                Jetzt buchen!
                            </a>
                            <a href="/#abfallarten" onClick={() => setIsMenuOpen(false)} className="text-gray-600 hover:text-gray-900 hover:bg-gray-50 font-medium py-3 px-3 rounded-lg transition-all">
                                Abfallarten
                            </a>
                            <a href="/#container" onClick={() => setIsMenuOpen(false)} className="text-gray-600 hover:text-gray-900 hover:bg-gray-50 font-medium py-3 px-3 rounded-lg transition-all">
                                Container
                            </a>
                            <a href="/#faq" onClick={() => setIsMenuOpen(false)} className="text-gray-600 hover:text-gray-900 hover:bg-gray-50 font-medium py-3 px-3 rounded-lg transition-all">
                                Kontakt
                            </a>
                            <Link to="/cart" className="bg-pink-600 text-white px-4 py-3 rounded-md hover:bg-pink-700 transition-colors font-bold inline-block text-center mt-2 uppercase">
                                Warenkorb
                            </Link>
                        </nav>
                    </div>
                )}
            </div>
        </header>
    );

    if (isLoading) return (
        <div className="min-h-screen bg-gray-50 flex items-center justify-center">
            <div className="text-center">
                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-pink-600 mx-auto mb-4"></div>
                <p className="text-lg text-gray-600">Warenkorb wird geladen...</p>
            </div>
        </div>
    );

    if (!cart || !cart.items?.length) return (
        <div className="min-h-screen bg-gray-50">
            {renderShopHeader()}



            <div className="container mx-auto px-4 py-16">
                <div className="max-w-2xl mx-auto text-center">
                    <h1 className="text-3xl font-bold text-gray-900 mb-8">Ihr Warenkorb</h1>
                    <div className="bg-white rounded-xl p-12 shadow-lg border border-gray-100">
                        <div className="mb-6">
                            <FontAwesomeIcon icon={faCartShopping} className="text-pink-600 text-6xl mb-4" />
                            <h2 className="text-xl font-semibold text-gray-700 mb-2">Ihr Warenkorb ist leer</h2>
                            <p className="text-gray-500">Fügen Sie Container zu Ihrem Warenkorb hinzu, um fortzufahren.</p>
                        </div>
                        <Link
                            to="/"
                            className="inline-block bg-pink-600 text-white px-8 py-3 rounded-lg font-semibold hover:bg-pink-700 transition duration-300"
                        >
                            Container buchen
                        </Link>
                    </div>
                </div>
            </div>
        </div>
    );

    const getMinStartDate = () => {
        const date = new Date();
        date.setDate(date.getDate() + 3);
        return date.toISOString().split('T')[0];
    };

    const handleEditItem = (item) => {
        if (isCartPending) {
            alert("Während eines laufenden Checkouts können keine Artikel bearbeitet werden.");
            return;
        }

        setEditingItem(item.id);
        setEditingItemData({
            quantity: item.quantity,
            startDate: item.startDate,
            endDate: item.endDate
        });
    };

    const handleCancelEdit = () => {
        setEditingItem(null);
        setEditingItemData({
            quantity: 1,
            startDate: '',
            endDate: ''
        });
    };

    const handleSaveItem = async (cartItemId) => {
        if (isCartPending) {
            alert("Während eines laufenden Checkouts können keine Artikel bearbeitet werden.");
            return;
        }

        const res = await fetch(`${API_BASE_URL}/cart/item/update`, {
            method: "PUT",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({
                cartItemId: cartItemId,
                quantity: editingItemData.quantity,
                startDate: editingItemData.startDate,
                endDate: editingItemData.endDate
            }),
            credentials: "include"
        });

        if (res.ok) {
            await refetch();
            handleCancelEdit();
        } else {
            alert("Fehler beim Aktualisieren des Artikels");
        }
    };

    const handleAddressSave = async () => {
        if (isCartPending) {
            alert("Während eines laufenden Checkouts können Adressen nicht geändert werden.");
            return;
        }

        await fetch(`${API_BASE_URL}/cart/address`, {
            method: "PUT",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({
                customerType,
                shippingAddress: shippingAddress,
                billingAddress: useDifferentBillingAddress ? billingAddress : shippingAddress
            }),
            credentials: "include"
        });
    }


    return (
        <div className="min-h-screen bg-gray-50">
            {renderShopHeader()}

            {showImportantNotesModal && (
                <div className="fixed inset-0 z-[100] bg-black/70 flex items-center justify-center p-0 sm:p-4">
                    <div className="relative w-full h-full sm:h-[92vh] sm:max-w-4xl bg-white sm:rounded-2xl shadow-2xl overflow-hidden flex flex-col">
                        <div className="bg-amber-50 border-b border-amber-200 px-5 sm:px-6 py-4">
                            <h2 className="text-lg sm:text-xl font-bold text-amber-900">Wichtige Hinweise für unsere Kunden</h2>
                        </div>

                        <div className="flex-1 min-h-0 overflow-y-auto p-5 sm:p-6 space-y-5 text-sm text-gray-800">
                            <p className="text-sm text-gray-700">Damit alles reibungslos abläuft, bitten wir Sie, folgende Punkte zu beachten:</p>

                            <div className="bg-gray-50 rounded-lg p-4 border border-gray-200">
                                <h3 className="font-semibold text-gray-900 mb-2">Bestellung &amp; Stornierung</h3>
                                <p className="text-gray-700 mb-1">Änderungen oder Stornierungen Ihres Auftrags müssen uns <strong>mindestens 24 Stunden vor dem Termin</strong> (Montag-Freitag) schriftlich mitgeteilt werden:</p>
                                <a href="mailto:container@hirsch-gmbh.com" className="text-pink-600 hover:underline font-medium">container@hirsch-gmbh.com</a>
                                <p className="text-gray-600 mt-1 text-xs">Bei späterer Absage müssen wir die entstandenen Transportkosten berechnen.</p>
                            </div>

                            <div className="bg-gray-50 rounded-lg p-4 border border-gray-200">
                                <h3 className="font-semibold text-gray-900 mb-2">Aufstellort des Containers</h3>
                                <ul className="space-y-1 text-gray-700 list-disc pl-5">
                                    <li>Bitte sorgen Sie für einen <strong>geeigneten und gut erreichbaren Stellplatz</strong>.</li>
                                    <li>Die Zufahrt muss sicher befahrbar sein.</li>
                                    <li>Unsere Fahrzeuge benötigen mindestens <strong>3 m Breite und 4 m Höhe</strong>.</li>
                                    <li>Für das Abstellen bzw. Abholen werden <strong>12-16 m Platz</strong> benötigt.</li>
                                    <li>Ist der Container nicht erreichbar, können zusätzliche Kosten entstehen.</li>
                                </ul>
                                <div className="mt-2 bg-gray-100 border border-gray-200 rounded p-2 text-xs text-gray-700">
                                    <strong>Wichtiger Hinweis:</strong> Der Container darf nach der Aufstellung <strong>nicht verschoben werden</strong>.
                                </div>
                            </div>

                            {isOutsideBremen && (
                                <div className="bg-gray-50 rounded-lg p-4 border border-gray-200">
                                    <h3 className="font-semibold text-gray-900 mb-2">Aufstellung im öffentlichen Bereich</h3>
                                    <p className="text-gray-700 mb-2">Wenn der Container auf einer Straße oder einem öffentlichen Platz steht:</p>
                                    <ul className="space-y-1 text-gray-700 list-disc pl-5">
                                        <li>Sie müssen eine <strong>Genehmigung einholen</strong> (über uns anfragen: <a href="mailto:container@hirsch-gmbh.com" className="text-pink-600 hover:underline">container@hirsch-gmbh.com</a>).</li>
                                        <li>Sie müssen für <strong>Absicherung sorgen</strong> (z. B. Beleuchtung, Absperrung).</li>
                                    </ul>
                                </div>
                            )}

                            <div className="bg-gray-50 rounded-lg p-4 border border-gray-200">
                                <h3 className="font-semibold text-gray-900 mb-2">Was darf in den Container?</h3>
                                <ul className="space-y-1 text-gray-700 list-disc pl-5">
                                    <li>Es dürfen nur die <strong>vereinbarten Abfälle</strong> eingefüllt werden.</li>
                                    <li>Die Container dürfen <strong>nicht über die Ladekante</strong> hinaus befüllt werden.</li>
                                </ul>
                                <p className="font-medium text-red-700 mt-3 mb-1">Gefährliche Stoffe sind verboten, z. B.:</p>
                                <ul className="space-y-0.5 text-gray-700 text-xs list-disc pl-5">
                                    <li>Asbest</li>
                                    <li>Mineralwolle (KMF)</li>
                                    <li>belastetes Holz (A4-Holz)</li>
                                </ul>
                                <p className="text-xs text-red-600 mt-2">Werden gefährliche Stoffe entsorgt, bleibt der Container kostenpflichtig stehen und wird nicht abgeholt.</p>
                            </div>

                            <div className="bg-gray-50 rounded-lg p-4 border border-gray-200">
                                <h3 className="font-semibold text-gray-900 mb-2">Weitere Hinweise</h3>
                                <ul className="space-y-2 text-gray-700 list-disc pl-5">
                                    <li><span className="font-medium">Änderung der Abfallart:</span> Der Inhalt darf nicht nachträglich geändert werden. Bei Abweichungen können zusätzliche Kosten entstehen.</li>
                                    <li><span className="font-medium">Verantwortung für den Inhalt:</span> Sie sind bis zur Abholung für den Inhalt des Containers verantwortlich.</li>
                                    <li><span className="font-medium">Besondere Abfälle:</span> Nicht vereinbarte gefährliche Abfälle sind nicht im Preis enthalten und werden separat berechnet.</li>
                                </ul>
                            </div>

                            <button
                                onClick={closeImportantNotesModal}
                                className="w-full px-4 py-3 rounded-lg bg-pink-600 border border-pink-600 text-white font-semibold hover:bg-pink-700 transition-colors"
                            >
                                Schließen
                            </button>
                        </div>
                    </div>
                </div>
            )}

            <div className="container mx-auto px-4 py-8">
                {/* Breadcrumb */}
                <nav className="mb-8">
                    <Link
                        to="/"
                        className="inline-flex items-center text-pink-600 hover:text-pink-700 font-medium"
                    >
                        <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                        </svg>
                        Zurück zur Startseite
                    </Link>
                </nav>

                <h1 className="text-3xl font-bold text-gray-900 mb-8">Bestätigen</h1>

                {isCartPending && (
                    <div className="mb-6 rounded-lg border border-amber-300 bg-amber-50 p-4 text-amber-900">
                        <p className="font-semibold">Zahlung in Bearbeitung</p>
                        <p className="text-sm mt-1">
                            Für diesen Warenkorb läuft bereits ein Checkout. Änderungen am Warenkorb sind aktuell gesperrt.
                        </p>

                        <div className="mt-3 flex flex-wrap gap-3">
                            <button
                                onClick={handleResumeCheckout}
                                disabled={!pendingCheckoutUrl}
                                className={`px-4 py-2 rounded-lg text-sm font-semibold ${pendingCheckoutUrl
                                    ? 'bg-amber-600 text-white hover:bg-amber-700'
                                    : 'bg-amber-200 text-amber-700 cursor-not-allowed'
                                    }`}
                            >
                                Weiter zur Zahlung
                            </button>
                            <button
                                onClick={handleCancelCheckout}
                                disabled={isCancellingCheckout}
                                className={`px-4 py-2 rounded-lg text-sm font-semibold ${isCancellingCheckout
                                    ? 'bg-gray-200 text-gray-600 cursor-not-allowed'
                                    : 'bg-red-600 text-white hover:bg-red-700'
                                    }`}
                            >
                                {isCancellingCheckout ? "Checkout wird abgebrochen..." : "Checkout abbrechen"}
                            </button>
                        </div>
                    </div>
                )}



                {/* Two-Column Layout */}
                <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
                    {/* Left Column - Cart Items */}
                    <div className="lg:col-span-2 space-y-4">
                        <div className="bg-white rounded-xl shadow-lg border border-gray-100 p-6">
                            <h2 className="text-xl font-bold text-gray-900 mb-4">{cart.items.length} Artikel</h2>

                            <div className="space-y-4">
                                {cart.items.map((item) => (
                                    <div
                                        key={item.id}
                                        className="bg-gray-50 rounded-lg p-4 border border-gray-200"
                                    >
                                        <div className="flex gap-4">
                                            {/* Product Image */}
                                            <div className="w-24 h-24 sm:w-32 sm:h-32 md:w-40 md:h-40 bg-white rounded-lg flex items-center justify-center flex-shrink-0">
                                                <img
                                                    src={item.images?.[0]?.base64Data || "/api/placeholder/80/80"}
                                                    alt={item.productName}
                                                    className="w-full h-full object-contain rounded-lg"
                                                    onError={(event) => {
                                                        event.currentTarget.src = "/api/placeholder/80/80";
                                                    }}
                                                />
                                            </div>

                                            {/* Product Details */}
                                            <div className="flex-1 min-w-0">
                                                {editingItem === item.id ? (
                                                    // Edit Mode
                                                    <div className="space-y-3">
                                                        <div>
                                                            <label className="text-sm font-medium text-gray-700 block mb-1">
                                                                Anzahl
                                                            </label>
                                                            <input
                                                                type="number"
                                                                min="1"
                                                                value={editingItemData.quantity}
                                                                onChange={(e) => setEditingItemData({
                                                                    ...editingItemData,
                                                                    quantity: parseInt(e.target.value) || 1
                                                                })}
                                                                className="w-20 sm:w-24 px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-pink-500"
                                                            />
                                                        </div>

                                                        {/* <div>
                                                            <label className="text-sm font-medium text-gray-700 block mb-1">
                                                                Bereitstellungszeitraum
                                                            </label>
                                                            <div className="flex flex-col sm:flex-row gap-2 items-start sm:items-center">
                                                                <input
                                                                    type="date"
                                                                    min={getMinStartDate()}
                                                                    value={editingItemData.startDate}
                                                                    onChange={(e) => setEditingItemData({
                                                                        ...editingItemData,
                                                                        startDate: e.target.value
                                                                    })}
                                                                    className="w-full sm:w-auto px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-pink-500 text-sm"
                                                                />
                                                                <span className="text-gray-500 text-sm sm:text-base">bis</span>
                                                                <input
                                                                    type="date"
                                                                    min={editingItemData.startDate}
                                                                    value={editingItemData.endDate}
                                                                    onChange={(e) => setEditingItemData({
                                                                        ...editingItemData,
                                                                        endDate: e.target.value
                                                                    })}
                                                                    className="w-full sm:w-auto px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-pink-500 text-sm"
                                                                />
                                                            </div>
                                                        </div> */}

                                                        <div className="flex flex-col sm:flex-row gap-2 mt-4">
                                                            <button
                                                                onClick={() => handleSaveItem(item.id)}
                                                                disabled={isCartPending}
                                                                className="w-full sm:w-auto px-4 py-2 bg-pink-600 text-white rounded-lg hover:bg-pink-700 transition-colors text-sm font-medium"
                                                            >
                                                                Speichern
                                                            </button>
                                                            <button
                                                                onClick={handleCancelEdit}
                                                                className="w-full sm:w-auto px-4 py-2 bg-gray-300 text-gray-700 rounded-lg hover:bg-gray-400 transition-colors text-sm font-medium"
                                                            >
                                                                Abbrechen
                                                            </button>
                                                        </div>
                                                    </div>
                                                ) : (
                                                    // Display Mode
                                                    <>
                                                        <div className="flex justify-between items-start mb-2">
                                                            <h3 className="font-semibold text-gray-900 text-sm sm:text-base">
                                                                Anzahl: {item.quantity}
                                                            </h3>
                                                            <button
                                                                onClick={() => handleEditItem(item)}
                                                                disabled={isCartPending}
                                                                className="text-pink-600 hover:text-pink-700 text-xs sm:text-sm font-medium whitespace-nowrap ml-2"
                                                            >
                                                                Bearbeiten
                                                            </button>
                                                        </div>
                                                        <p className="text-xs sm:text-sm text-gray-600 mt-1">
                                                            {item.containerName}: {((item.totalPriceCents - item.deliveryAreaPriceCents - item.durationPriceCents - item.wasteTypePriceCents) / 100).toFixed(2)} €
                                                        </p>
                                                        {/* Bereitstellungzeitraum von bis */}
                                                        <p className="text-xs sm:text-sm text-gray-600">
                                                            Bereitstellungszeitraum: {new Date(item.startDate).toLocaleDateString("de-DE")} - {new Date(item.endDate).toLocaleDateString("de-DE")}
                                                        </p>
                                                        <p className="text-xs sm:text-sm text-gray-600">
                                                            {/* Only display inklusive if shipping is not required */}
                                                            Lieferkosten: {!item.requiresShipping && "inklusive"}
                                                        </p>

                                                        <div className="mt-2 text-base sm:text-lg font-bold text-gray-900">
                                                            {(item.totalPriceCents / 100).toFixed(2)} €
                                                        </div>
                                                    </>
                                                )}
                                            </div>

                                            {/* Remove Button */}
                                            <button
                                                onClick={() => handleRemoveFromCart(item.id)}
                                                disabled={isCartPending}
                                                className="self-start p-2 text-pink-500 hover:text-red-500 transition-colors flex-shrink-0"
                                                title="Entfernen"
                                            >
                                                <FontAwesomeIcon icon={faTrash} className="text-base sm:text-lg" />
                                            </button>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        </div>

                    </div>

                    {/* Right Column - Address Information */}
                    <div className="lg:col-span-1 space-y-4">
                        {/* Shipping Address */}
                        <div className="bg-white rounded-xl shadow-lg border border-gray-100 p-6">
                            <div className="flex items-center justify-between mb-4">
                                <h2 className="text-lg font-semibold text-gray-900">Aufstelladresse</h2>
                                <button
                                    onClick={() => {
                                        if (isCartPending) {
                                            return;
                                        }
                                        setEditingShipping(!editingShipping)
                                        editingShipping && handleAddressSave();
                                    }}
                                    disabled={isCartPending}
                                    className="text-pink-600 hover:text-pink-700"
                                >
                                    {editingShipping ? (
                                        <span className="text-sm font-medium" >Speichern</span>
                                    ) : (
                                        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15.232 5.232l3.536 3.536m-2.036-5.036a2.5 2.5 0 113.536 3.536L6.5 21.036H3v-3.572L16.732 3.732z" />
                                        </svg>
                                    )}
                                </button>
                            </div>

                            {editingShipping ? (
                                <div className="space-y-3">
                                    <div className="">
                                        <h2 className="text-lg font-semibold text-gray-900 mb-4">Kundentyp</h2>
                                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                                            <label className="flex items-center p-3 border border-gray-300 rounded-lg cursor-pointer hover:border-pink-400">
                                                <input
                                                    type="radio"
                                                    name="customerType"
                                                    value="private"
                                                    checked={customerType === "private"}
                                                    onChange={() => setCustomerType("private")}
                                                    className="mr-3 w-4 h-4 text-pink-600 focus:ring-pink-500"
                                                />
                                                <span className="text-sm font-medium text-gray-800">Privatkunde</span>
                                            </label>
                                            <label className="flex items-center p-3 border border-gray-300 rounded-lg cursor-pointer hover:border-pink-400">
                                                <input
                                                    type="radio"
                                                    name="customerType"
                                                    value="business"
                                                    checked={customerType === "business"}
                                                    onChange={() => setCustomerType("business")}
                                                    className="mr-3 w-4 h-4 text-pink-600 focus:ring-pink-500"
                                                />
                                                <span className="text-sm font-medium text-gray-800">Gewerbekunde</span>
                                            </label>
                                        </div>
                                    </div>
                                    <select
                                        value={shippingAddress.salutation}
                                        onChange={(e) => setShippingAddress({ ...shippingAddress, salutation: e.target.value })}
                                        className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-pink-500"
                                    >
                                        {SALUTATION_OPTIONS.map((option) => (
                                            <option key={option || "leer"} value={option}>
                                                {option || "Anrede auswählen"}
                                            </option>
                                        ))}
                                    </select>
                                    {customerType === "business" ? (
                                        <>
                                            <input
                                                type="text"
                                                placeholder="Firmenname"
                                                value={shippingAddress.companyName}
                                                onChange={(e) => setShippingAddress({ ...shippingAddress, companyName: e.target.value })}
                                                className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-pink-500"
                                            />
                                            <input
                                                type="text"
                                                placeholder="Steueridentifikationsnummer"
                                                value={shippingAddress.companyTaxId}
                                                onChange={(e) => setShippingAddress({ ...shippingAddress, companyTaxId: e.target.value })}
                                                className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-pink-500"
                                            />
                                            <input
                                                type="text"
                                                placeholder="Handelsregisternummer"
                                                value={shippingAddress.companyRegisterId}
                                                onChange={(e) => setShippingAddress({ ...shippingAddress, companyRegisterId: e.target.value })}
                                                className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-pink-500"
                                            />
                                            <div className="grid grid-cols-2 gap-3">
                                                <input
                                                    type="text"
                                                    placeholder="Ansprechpartner Vorname"
                                                    value={shippingAddress.firstName}
                                                    onChange={(e) => setShippingAddress({ ...shippingAddress, firstName: e.target.value })}
                                                    className="px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-pink-500"
                                                />
                                                <input
                                                    type="text"
                                                    placeholder="Ansprechpartner Nachname"
                                                    value={shippingAddress.lastName}
                                                    onChange={(e) => setShippingAddress({ ...shippingAddress, lastName: e.target.value })}
                                                    className="px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-pink-500"
                                                />
                                            </div>
                                        </>
                                    ) : (
                                        <div className="grid grid-cols-2 gap-3">
                                            <input
                                                type="text"
                                                placeholder="Vorname"
                                                value={shippingAddress.firstName}
                                                onChange={(e) => setShippingAddress({ ...shippingAddress, firstName: e.target.value })}
                                                className="px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-pink-500"
                                            />
                                            <input
                                                type="text"
                                                placeholder="Nachname"
                                                value={shippingAddress.lastName}
                                                onChange={(e) => setShippingAddress({ ...shippingAddress, lastName: e.target.value })}
                                                className="px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-pink-500"
                                            />
                                        </div>
                                    )}
                                    <input
                                        type="text"
                                        placeholder="Straße und Hausnummer"
                                        value={shippingAddress.street}
                                        onChange={(e) => setShippingAddress({ ...shippingAddress, street: e.target.value })}
                                        className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-pink-500"
                                    />
                                    <div className="grid grid-cols-2 gap-3">
                                        <input
                                            type="text"
                                            placeholder="PLZ"
                                            value={shippingAddress.zipCode}
                                            onChange={(e) => setShippingAddress({ ...shippingAddress, zipCode: e.target.value })}
                                            className="px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-pink-500"
                                            disabled={true}
                                        />
                                        <input
                                            type="text"
                                            placeholder="Stadt"
                                            value={shippingAddress.city}
                                            onChange={(e) => setShippingAddress({ ...shippingAddress, city: e.target.value })}
                                            className="px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-pink-500"
                                        />
                                    </div>
                                    <input
                                        type="text"
                                        placeholder="Land"
                                        value={shippingAddress.country}
                                        onChange={(e) => setShippingAddress({ ...shippingAddress, country: e.target.value })}
                                        className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-pink-500"
                                    />
                                    <input
                                        type="tel"
                                        placeholder="Telefon"
                                        value={shippingAddress.phone}
                                        onChange={(e) => setShippingAddress({ ...shippingAddress, phone: e.target.value })}
                                        className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-pink-500"
                                    />
                                    <input
                                        type="email"
                                        placeholder="E-Mail"
                                        value={shippingAddress.email}
                                        onChange={(e) => setShippingAddress({ ...shippingAddress, email: e.target.value })}
                                        className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-pink-500"
                                    />
                                </div>
                            ) : (
                                <div className="text-sm text-gray-700 space-y-1">
                                    {shippingAddress.salutation && <p>{shippingAddress.salutation}</p>}
                                    {shippingAddress.companyName && <p className="font-medium">{shippingAddress.companyName}</p>}
                                    {shippingAddress.firstName || shippingAddress.lastName ? (
                                        <p className="font-medium">{shippingAddress.firstName} {shippingAddress.lastName}</p>
                                    ) : null}
                                    {shippingAddress.street && <p>{shippingAddress.street}</p>}
                                    {(shippingAddress.zipCode || shippingAddress.city) && (
                                        <p>{shippingAddress.zipCode} {shippingAddress.city}</p>
                                    )}
                                    {shippingAddress.country && <p>{shippingAddress.country}</p>}
                                    {shippingAddress.phone && <p>{shippingAddress.phone}</p>}
                                    {shippingAddress.email && <p>{shippingAddress.email}</p>}
                                    {!shippingAddress.firstName && !shippingAddress.street && (
                                        <p className="text-gray-400 italic">Bitte Adresse eingeben</p>
                                    )}
                                </div>
                            )}
                        </div>

                        {/* Billing Address */}
                        <div className="bg-white rounded-xl shadow-lg border border-gray-100 p-6">
                            <div className="flex items-center justify-between mb-4">
                                <h2 className="text-lg font-semibold text-gray-900">Rechnungsadresse</h2>
                                <button
                                    onClick={() => {
                                        if (isCartPending) {
                                            return;
                                        }
                                        setEditingBilling(!editingBilling);
                                        editingBilling && handleAddressSave();
                                    }}
                                    disabled={isCartPending}
                                    className="text-pink-600 hover:text-pink-700"
                                >
                                    {editingBilling ? (
                                        <span className="text-sm font-medium">Speichern</span>
                                    ) : (
                                        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15.232 5.232l3.536 3.536m-2.036-5.036a2.5 2.5 0 113.536 3.536L6.5 21.036H3v-3.572L16.732 3.732z" />
                                        </svg>
                                    )}
                                </button>
                            </div>

                            <div className="mb-3">
                                <label className="flex items-center text-sm text-gray-700 cursor-pointer">
                                    <input
                                        type="checkbox"
                                        checked={!useDifferentBillingAddress}
                                        onChange={(e) => setUseDifferentBillingAddress(!e.target.checked)}
                                        disabled={isCartPending}
                                        className="mr-2 w-4 h-4 text-pink-600 rounded focus:ring-pink-500"
                                    />
                                    Entspricht der Aufstelladresse
                                </label>
                            </div>

                            {useDifferentBillingAddress && (
                                editingBilling ? (
                                    <div className="space-y-3">
                                        <div>
                                            <h2 className="text-lg font-semibold text-gray-900 mb-4">Rechnungsempfänger</h2>
                                            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                                                <label className="flex items-center p-3 border border-gray-300 rounded-lg cursor-pointer hover:border-pink-400">
                                                    <input
                                                        type="radio"
                                                        name="billingCustomerType"
                                                        value="private"
                                                        checked={billingCustomerType === "private"}
                                                        onChange={() => setBillingCustomerType("private")}
                                                        className="mr-3 w-4 h-4 text-pink-600 focus:ring-pink-500"
                                                    />
                                                    <span className="text-sm font-medium text-gray-800">Privatkunde</span>
                                                </label>
                                                <label className="flex items-center p-3 border border-gray-300 rounded-lg cursor-pointer hover:border-pink-400">
                                                    <input
                                                        type="radio"
                                                        name="billingCustomerType"
                                                        value="business"
                                                        checked={billingCustomerType === "business"}
                                                        onChange={() => setBillingCustomerType("business")}
                                                        className="mr-3 w-4 h-4 text-pink-600 focus:ring-pink-500"
                                                    />
                                                    <span className="text-sm font-medium text-gray-800">Gewerbekunde</span>
                                                </label>
                                            </div>
                                        </div>

                                        {billingCustomerType === "business" ? (
                                            <>
                                                <input
                                                    type="text"
                                                    placeholder="Firmenname"
                                                    value={billingAddress.companyName}
                                                    onChange={(e) => setBillingAddress({ ...billingAddress, companyName: e.target.value })}
                                                    className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-pink-500"
                                                />
                                                <input
                                                    type="text"
                                                    placeholder="Steueridentifikationsnummer"
                                                    value={billingAddress.companyTaxId}
                                                    onChange={(e) => setBillingAddress({ ...billingAddress, companyTaxId: e.target.value })}
                                                    className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-pink-500"
                                                />
                                                <input
                                                    type="text"
                                                    placeholder="Handelsregisternummer"
                                                    value={billingAddress.companyRegisterId}
                                                    onChange={(e) => setBillingAddress({ ...billingAddress, companyRegisterId: e.target.value })}
                                                    className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-pink-500"
                                                />
                                                <select
                                                    value={billingAddress.salutation}
                                                    onChange={(e) => setBillingAddress({ ...billingAddress, salutation: e.target.value })}
                                                    className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-pink-500"
                                                >
                                                    {SALUTATION_OPTIONS.map((option) => (
                                                        <option key={`${option || "leer"}-billing`} value={option}>
                                                            {option || "Anrede auswählen"}
                                                        </option>
                                                    ))}
                                                </select>
                                                <div className="grid grid-cols-2 gap-3">
                                                    <input
                                                        type="text"
                                                        placeholder="Ansprechpartner Vorname"
                                                        value={billingAddress.firstName}
                                                        onChange={(e) => setBillingAddress({ ...billingAddress, firstName: e.target.value })}
                                                        className="px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-pink-500"
                                                    />
                                                    <input
                                                        type="text"
                                                        placeholder="Ansprechpartner Nachname"
                                                        value={billingAddress.lastName}
                                                        onChange={(e) => setBillingAddress({ ...billingAddress, lastName: e.target.value })}
                                                        className="px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-pink-500"
                                                    />
                                                </div>
                                            </>
                                        ) : (
                                            <>
                                                <select
                                                    value={billingAddress.salutation}
                                                    onChange={(e) => setBillingAddress({ ...billingAddress, salutation: e.target.value })}
                                                    className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-pink-500"
                                                >
                                                    {SALUTATION_OPTIONS.map((option) => (
                                                        <option key={`${option || "leer"}-billing`} value={option}>
                                                            {option || "Anrede auswählen"}
                                                        </option>
                                                    ))}
                                                </select>
                                                <div className="grid grid-cols-2 gap-3">
                                                    <input
                                                        type="text"
                                                        placeholder="Vorname"
                                                        value={billingAddress.firstName}
                                                        onChange={(e) => setBillingAddress({ ...billingAddress, firstName: e.target.value })}
                                                        className="px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-pink-500"
                                                    />
                                                    <input
                                                        type="text"
                                                        placeholder="Nachname"
                                                        value={billingAddress.lastName}
                                                        onChange={(e) => setBillingAddress({ ...billingAddress, lastName: e.target.value })}
                                                        className="px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-pink-500"
                                                    />
                                                </div>
                                            </>
                                        )}
                                        <input
                                            type="text"
                                            placeholder="Straße und Hausnummer"
                                            value={billingAddress.street}
                                            onChange={(e) => setBillingAddress({ ...billingAddress, street: e.target.value })}
                                            className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-pink-500"
                                        />
                                        <div className="grid grid-cols-2 gap-3">
                                            <input
                                                type="text"
                                                placeholder="PLZ"
                                                value={billingAddress.zipCode}
                                                onChange={(e) => setBillingAddress({ ...billingAddress, zipCode: e.target.value })}
                                                className="px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-pink-500"
                                            />
                                            <input
                                                type="text"
                                                placeholder="Stadt"
                                                value={billingAddress.city}
                                                onChange={(e) => setBillingAddress({ ...billingAddress, city: e.target.value })}
                                                className="px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-pink-500"
                                            />
                                        </div>
                                        <input
                                            type="text"
                                            placeholder="Land"
                                            value={billingAddress.country}
                                            onChange={(e) => setBillingAddress({ ...billingAddress, country: e.target.value })}
                                            className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-pink-500"
                                        />
                                        <input
                                            type="tel"
                                            placeholder="Telefon (optional)"
                                            value={billingAddress.phone}
                                            onChange={(e) => setBillingAddress({ ...billingAddress, phone: e.target.value })}
                                            className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-pink-500"
                                        />
                                        <input
                                            type="email"
                                            placeholder="E-Mail"
                                            value={billingAddress.email}
                                            onChange={(e) => setBillingAddress({ ...billingAddress, email: e.target.value })}
                                            className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-pink-500"
                                        />
                                    </div>
                                ) : (
                                    <div className="text-sm text-gray-700 space-y-1">
                                        {billingAddress.salutation && <p>{billingAddress.salutation}</p>}
                                        {billingAddress.companyName && <p className="font-medium">{billingAddress.companyName}</p>}
                                        {billingAddress.firstName || billingAddress.lastName ? (
                                            <p className="font-medium">{billingAddress.firstName} {billingAddress.lastName}</p>
                                        ) : null}
                                        {billingAddress.street && <p>{billingAddress.street}</p>}
                                        {(billingAddress.zipCode || billingAddress.city) && (
                                            <p>{billingAddress.zipCode} {billingAddress.city}</p>
                                        )}
                                        {billingAddress.country && <p>{billingAddress.country}</p>}
                                        {billingAddress.phone && <p>{billingAddress.phone}</p>}
                                        {billingAddress.email && <p>{billingAddress.email}</p>}
                                        {!billingAddress.firstName && !billingAddress.street && (
                                            <p className="text-gray-400 italic">Bitte Adresse eingeben</p>
                                        )}
                                    </div>
                                )
                            )}
                        </div>



                        {/* Price Summary */}
                        <div className="bg-white rounded-xl shadow-lg border border-gray-100 p-6">
                            <div className="space-y-3">




                                {cart.items.some(item => item.requiresShipping) && (
                                    <div className="pt-3 mt-3">
                                        <div className="flex justify-between items-center">
                                            {/* Display 5€ Lieferkosten once if there is at least one item that requires shipping */}

                                            <span className="font-bold text-gray-900">Lieferkosten</span>
                                            <span className="text-2xl font-bold text-pink-600">
                                                {/* LIEFERKOSTEN */}
                                                {cart.items.some(item => item.requiresShipping) ? (process.env.REACT_APP_SHIPPING_COSTS_DISPLAY / 100).toFixed(2) : (0 / 100).toFixed(2)} €
                                            </span>
                                        </div>
                                    </div>
                                )}
                                <div className="pt-1 mt-1">
                                    <div className="flex justify-between items-center">
                                        {/* Display 5€ Lieferkosten once if there is at least one item that requires shipping */}

                                        <span className="font-bold text-gray-900">Gesamtsumme</span>
                                        <span className="text-2xl font-bold text-pink-600">
                                            {/* LIEFERKOSTEN */}
                                            {cart.items.some(item => item.requiresShipping) ? ((cart.totalPriceCents + parseInt(process.env.REACT_APP_SHIPPING_COSTS_DISPLAY)) / 100).toFixed(2) : (cart.totalPriceCents / 100).toFixed(2)} €
                                        </span>
                                    </div>
                                </div>
                            </div>



                            {/* Confirmation Checkboxes */}
                            <div className="mt-5 space-y-3 border-t border-gray-100 pt-4">
                                <button
                                    onClick={openImportantNotesModal}
                                    className="w-full px-4 py-3 rounded-lg bg-amber-100 border border-amber-300 text-amber-900 font-semibold hover:bg-amber-200 transition-colors"
                                >
                                    Wichtige Hinweise lesen
                                </button>

                                {!hasOpenedImportantNotesModal && (
                                    <p className="text-xs text-amber-700">
                                        Bitte lesen Sie die wichtigen Hinweise mindestens einmal. Danach können Sie die Checkboxen bestätigen.
                                    </p>
                                )}

                                <label className={`flex items-start gap-3 cursor-pointer rounded-lg p-3 transition-colors ${acceptedConditions1 ? 'bg-green-50 border border-green-200' : 'bg-gray-50 border border-gray-200'}`}>
                                    <input
                                        type="checkbox"
                                        checked={acceptedConditions1}
                                        onChange={(e) => handleConditionsToggle(1, e.target.checked)}
                                        disabled={isCartPending}
                                        className="mt-0.5 w-4 h-4 text-pink-600 rounded focus:ring-pink-500 flex-shrink-0 cursor-pointer"
                                    />
                                    <span className="text-xs text-gray-700 leading-relaxed">
                                        Ich habe die <strong>wichtigen Hinweise</strong> gelesen und stimme den Bedingungen zu Bestellung, Stornierung, Aufstellort und Containerinhalt zu.
                                    </span>
                                </label>
                                <label className={`flex items-start gap-3 cursor-pointer rounded-lg p-3 transition-colors ${acceptedConditions2 ? 'bg-green-50 border border-green-200' : 'bg-gray-50 border border-gray-200'}`}>
                                    <input
                                        type="checkbox"
                                        checked={acceptedConditions2}
                                        onChange={(e) => handleConditionsToggle(2, e.target.checked)}
                                        disabled={isCartPending}
                                        className="mt-0.5 w-4 h-4 text-pink-600 rounded focus:ring-pink-500 flex-shrink-0 cursor-pointer"
                                    />
                                    <span className="text-xs text-gray-700 leading-relaxed">
                                        Ich bestätige, dass der Stellplatz geeignet und erreichbar ist (mind. <strong>3&nbsp;m Breite, 4&nbsp;m Höhe</strong>, 12–16&nbsp;m Platz) und ich <strong>keine gefährlichen Stoffe</strong> entsorgen werde.
                                    </span>
                                </label>
                            </div>

                            {/* Checkout Button */}
                            <button
                                onClick={handleCheckout}
                                disabled={!isCheckoutEnabled()}
                                className={`w-full mt-4 px-6 py-4 rounded-lg font-semibold text-lg transition-all duration-300 shadow-lg ${isCheckoutEnabled()
                                    ? 'bg-gradient-to-r from-pink-600 to-pink-700 text-white hover:from-pink-700 hover:to-pink-800 cursor-pointer'
                                    : 'bg-gray-300 text-gray-500 cursor-not-allowed'
                                    }`}
                            >
                                Weiter zur Zahlung
                            </button>
                            {!isCheckoutEnabled() && (
                                <p className={`text-xs mt-2 text-center ${checkoutHint.isError ? 'text-red-600' : 'text-gray-500'}`}>
                                    {checkoutHint.message}
                                </p>
                            )}
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
}
